/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Connector App and Organization Management APIs
 * @module Epic.AppOrchard.ConnectorManagement.Data
 */
import { IGenericAPIResponseWithId, IMergeConnectorAppAPIResponse } from "ao/data";
import {
	ConnectorAppEditData,
	IAppMergeModel,
	ICategoryForConnectorApp,
	IConnectorApp,
	IConnectorAppListItem,
	IConnectorAppMergeMessageBanners,
	IConnectorAppWithVendor,
	IConnectorManagementLookups,
	IDeleteRequest,
	IRestoreRequest,
} from "ao/types/connectorManagement";
import api, { fileApi } from "ao/utils/api";
import { getInstantForRecordExport } from "ao/utils/helpers";

export async function getConnectorManagementLookups(): Promise<IConnectorManagementLookups> {
	return await api.get<IConnectorManagementLookups>("/api/Connector/GetConnectorManagementLookups");
}

export async function getConnectorAppMergeMessageBanners(): Promise<IConnectorAppMergeMessageBanners> {
	return await api.get<IConnectorAppMergeMessageBanners>(
		"/api/Connector/GetConnectorAppMergeMessageBanners",
	);
}

export async function getConnectorApp(id: number): Promise<IConnectorApp> {
	return await api.get<IConnectorApp>(`/api/Connector/GetApp?id=${id}`);
}

export async function getConnectorAppEditData(appId: number): Promise<ConnectorAppEditData> {
	const promises: [Promise<IConnectorApp>, Promise<IConnectorManagementLookups>] = [
		getConnectorApp(appId),
		getConnectorManagementLookups(),
	];
	return Promise.all(promises);
}

export async function getCategoriesForConnectorApp(id: number): Promise<ICategoryForConnectorApp[]> {
	return await api.get<ICategoryForConnectorApp[]>(`/api/Connector/GetCategoriesForApp/${id}`);
}

export async function getVendorsForConnectorApps(ids: number[]): Promise<IConnectorAppWithVendor[]> {
	return await api.get<IConnectorAppWithVendor[]>(
		`/api/Connector/GetVendorsForApps?ids=${ids.join("&ids=")}`,
	);
}

export interface IRecordList {
	recordList: IConnectorAppListItem[];
	recordTotal: number;
}

export async function getConnectorApps(listParameterRecords: Record<string, string>): Promise<IRecordList> {
	return await api.get<IRecordList>("/api/Connector/GetConnectorAppList", listParameterRecords);
}

export async function getFilteredConnectorAppExport(
	listParameterRecords: Record<string, string>,
): Promise<void> {
	return await fileApi.get<void>(
		"/ConnectorManagementExport/FilteredConnectorApps",
		"text/csv",
		`ConnectorAppsExport-${getInstantForRecordExport()}.csv`,
		listParameterRecords,
	);
}

export async function saveConnectorApp(data: IConnectorApp): Promise<IGenericAPIResponseWithId> {
	return await api.post<IGenericAPIResponseWithId>("/api/Connector/SaveApp", data);
}

export async function mergeConnectorApps(data: IAppMergeModel): Promise<IMergeConnectorAppAPIResponse> {
	return await api.post<IMergeConnectorAppAPIResponse>("api/Connector/MergeApps", data);
}

export async function deleteConnectorApp(data: IDeleteRequest): Promise<IGenericAPIResponseWithId> {
	return await api.post<IGenericAPIResponseWithId>("/api/Connector/DeleteApp", data);
}

export async function restoreConnectorApp(data: IRestoreRequest): Promise<IGenericAPIResponseWithId> {
	return await api.post<IGenericAPIResponseWithId>("/api/Connector/RestoreApp", data);
}
